import { ImageFile, OriginalLine, OBBAnnotations, Point } from '../types';

export const findMatchingCSV = (imageName: string, files: FileList): File | null => {
  const baseName = imageName.replace(/\.(jpg|jpeg|png|bmp)$/i, '');
  
  for (let i = 0; i < files.length; i++) {
    const file = files[i];
    if (file.name.toLowerCase().endsWith('.csv')) {
      const csvBaseName = file.name.replace(/\.csv$/i, '');
      if (csvBaseName === baseName) {
        return file;
      }
    }
  }
  
  return null;
};

export const loadImageFiles = async (files: FileList): Promise<ImageFile[]> => {
  const imageFiles: ImageFile[] = [];
  
  for (let i = 0; i < files.length; i++) {
    const file = files[i];
    
    if (file.name.match(/\.(jpg|jpeg|png|bmp)$/i)) {
      const matchingCSV = findMatchingCSV(file.name, files);
      
      imageFiles.push({
        name: file.name,
        path: URL.createObjectURL(file),
        csvPath: matchingCSV ? URL.createObjectURL(matchingCSV) : undefined
      });
    }
  }
  
  return imageFiles.sort((a, b) => a.name.localeCompare(b.name));
};

export const parseOriginalCSV = async (csvFile: File | string): Promise<OriginalLine[]> => {
  try {
    let text: string;
    
    if (typeof csvFile === 'string') {
      const response = await fetch(csvFile);
      text = await response.text();
    } else {
      text = await csvFile.text();
    }
    
    const lines = text.trim().split('\n');
    
    return lines.map(line => {
      const values = line.split(',').map(val => parseFloat(val.trim()));
      return {
        x1: values[0] || 0,
        y1: values[1] || 0,
        x2: values[2] || 0,
        y2: values[3] || 0,
        class: values[4]
      };
    }).filter(line => !isNaN(line.x1) && !isNaN(line.y1) && !isNaN(line.x2) && !isNaN(line.y2));
  } catch (error) {
    console.error('解析CSV檔案時發生錯誤:', error);
    return [];
  }
};

// Parse original CSV and group L1a, L1b etc. into quadrilaterals for OBB mode
export const parseOriginalCSVForOBB = async (csvFile: File | string): Promise<{ [vertebraId: string]: Point[] }> => {
  try {
    let text: string;
    
    if (typeof csvFile === 'string') {
      const response = await fetch(csvFile);
      text = await response.text();
    } else {
      text = await csvFile.text();
    }
    
    const lines = text.trim().split('\n');
    const vertebraGroups: { [key: string]: OriginalLine[] } = {};
    
    // Parse lines with vertebra identification
    lines.forEach((line, index) => {
      const values = line.split(',').map(val => val.trim());
      
      const x1 = parseFloat(values[0]);
      const y1 = parseFloat(values[1]);
      const x2 = parseFloat(values[2]);
      const y2 = parseFloat(values[3]);
      const classNum = parseFloat(values[4]);
      
      if (!isNaN(x1) && !isNaN(y1) && !isNaN(x2) && !isNaN(y2) && !isNaN(classNum)) {
        // Map lines sequentially to vertebra identifiers
        // Assumes lines are ordered as L1a, L1b, L2a, L2b, etc.
        const vertebraIds = [
          'L1a', 'L1b', 'L2a', 'L2b', 'L3a', 'L3b', 
          'L4a', 'L4b', 'L5a', 'L5b'
        ];
        
        // Use index to determine vertebra
        let vertebraId: string | undefined;
        if (index < vertebraIds.length) {
          vertebraId = vertebraIds[index];
        }
        
        if (vertebraId) {
          if (!vertebraGroups[vertebraId]) {
            vertebraGroups[vertebraId] = [];
          }
          vertebraGroups[vertebraId].push({ x1, y1, x2, y2, class: classNum, vertebra: vertebraId });
        }
      }
    });
    
    // Combine pairs (L1a + L1b) into quadrilaterals
    const quadrilaterals: { [vertebraId: string]: Point[] } = {};
    const vertebraLevels = ['L1', 'L2', 'L3', 'L4', 'L5'];
    
    vertebraLevels.forEach(level => {
      const aLines = vertebraGroups[`${level}a`] || [];
      const bLines = vertebraGroups[`${level}b`] || [];
      
      // Skip S1 as requested
      if (level === 'S1') return;
      
      if (aLines.length > 0 && bLines.length > 0) {
        // Take the first line from each group
        const aLine = aLines[0];
        const bLine = bLines[0];
        
        // Combine the 4 points from both lines into a proper quadrilateral
        // Arrange points in clockwise order to avoid self-intersection
        // Assuming L1a is upper edge and L1b is lower edge of the vertebra
        const points: Point[] = [
          { x: aLine.x1, y: aLine.y1 },  // Upper left
          { x: aLine.x2, y: aLine.y2 },  // Upper right  
          { x: bLine.x2, y: bLine.y2 },  // Lower right
          { x: bLine.x1, y: bLine.y1 }   // Lower left
        ];
        
        quadrilaterals[level] = points;
      }
    });
    
    return quadrilaterals;
  } catch (error) {
    console.error('解析OBB CSV檔案時發生錯誤:', error);
    return {};
  }
};

export const exportTriangleCSV = (annotations: any, imageFileName?: string): void => {
  const csvData: string[] = [];
  const headers = ['vertebra', 'edge', 'point1_x', 'point1_y', 'point2_x', 'point2_y', 'point3_x', 'point3_y'];
  csvData.push(headers.join(','));

  const vertebrae = ['L1', 'L2', 'L3', 'L4', 'L5'] as const;
  
  vertebrae.forEach(vertebra => {
    if (annotations[vertebra]) {
      ['upper', 'lower'].forEach(edge => {
        if (annotations[vertebra][edge]) {
          const triangle = annotations[vertebra][edge];
          const row = [
            vertebra,
            edge,
            triangle.points[0].x.toFixed(2),
            triangle.points[0].y.toFixed(2),
            triangle.points[1].x.toFixed(2),
            triangle.points[1].y.toFixed(2),
            triangle.points[2].x.toFixed(2),
            triangle.points[2].y.toFixed(2)
          ];
          csvData.push(row.join(','));
        }
      });
    }
  });

  // 生成檔名：使用原始圖片檔名或預設名稱
  let fileName = 'vertebra_triangles.csv';
  if (imageFileName) {
    // 移除副檔名並添加 _triangles.csv
    const baseName = imageFileName.replace(/\.(jpg|jpeg|png|bmp)$/i, '');
    fileName = `${baseName}_triangles.csv`;
  }

  const csvContent = csvData.join('\n');
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  const url = URL.createObjectURL(blob);
  link.setAttribute('href', url);
  link.setAttribute('download', fileName);
  link.style.visibility = 'hidden';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

export const exportOBBCSV = (obbAnnotations: OBBAnnotations, imageFileName?: string): void => {
  const csvData: string[] = [];
  const headers = ['vertebra_id', 'view_angle', 'x1', 'y1', 'x2', 'y2', 'x3', 'y3', 'x4', 'y4'];
  csvData.push(headers.join(','));
  
  // 中文視角轉英文映射
  const viewAngleMap: { [key: string]: string } = {
    '正視': 'frontal',
    '俯視': 'top',
    '仰視': 'bottom'
  };
  
  Object.entries(obbAnnotations).forEach(([vertebraId, annotation]) => {
    const quad = annotation.quadrilateral;
    const englishViewAngle = viewAngleMap[annotation.viewAngle] || annotation.viewAngle;
    
    const row = [
      vertebraId,
      englishViewAngle,
      quad.points[0].x.toFixed(2),
      quad.points[0].y.toFixed(2),
      quad.points[1].x.toFixed(2),
      quad.points[1].y.toFixed(2),
      quad.points[2].x.toFixed(2),
      quad.points[2].y.toFixed(2),
      quad.points[3].x.toFixed(2),
      quad.points[3].y.toFixed(2)
    ];
    csvData.push(row.join(','));
  });

  // 生成檔名：使用原始圖片檔名或預設名稱
  let fileName = 'vertebra_obb.csv';
  if (imageFileName) {
    // 移除副檔名並添加 _obb.csv
    const baseName = imageFileName.replace(/\.(jpg|jpeg|png|bmp)$/i, '');
    fileName = `${baseName}_obb.csv`;
  }

  const csvContent = csvData.join('\n');
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  const url = URL.createObjectURL(blob);
  link.setAttribute('href', url);
  link.setAttribute('download', fileName);
  link.style.visibility = 'hidden';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

export const loadImage = (imagePath: string): Promise<HTMLImageElement> => {
  return new Promise((resolve, reject) => {
    const image = new Image();
    image.onload = () => resolve(image);
    image.onerror = reject;
    image.src = imagePath;
  });
};